/**
 * Unified facts explorer - browse all facts (own + public when authenticated, public only otherwise)
 * Supports grouping by source URL, text search, visibility and status filtering.
 */

import { useState, useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { resultsApi, favouritesApi, tagsApi, projectsApi } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import { LoadingSpinner } from '../components/LoadingSpinner'
import type { VerdictCategory, VerificationListItem } from '../types'
import { ChipRow, SCOPE_AMBER, toChipKeys } from '../components/factChips'

type StatusFilter = 'all' | 'verified' | 'refuted'
type VisibilityFilter = 'all' | 'private' | 'my_public' | 'all_public'
type ViewMode = 'flat' | 'grouped'

interface SourceGroup {
  sourceUrl: string
  displayHost: string
  results: VerificationListItem[]
}

/** Extract a readable hostname from a URL */
const getDisplayHost = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

/** Group results by source URL. Results without a source URL go into an "ungrouped" bucket. */
const groupBySource = (results: VerificationListItem[]): SourceGroup[] => {
  const groups = new Map<string, VerificationListItem[]>()
  const ungrouped: VerificationListItem[] = []

  for (const result of results) {
    const url = result.source_url
    if (url) {
      const existing = groups.get(url)
      if (existing) {
        existing.push(result)
      } else {
        groups.set(url, [result])
      }
    } else {
      ungrouped.push(result)
    }
  }

  const sourceGroups: SourceGroup[] = []
  for (const [url, items] of groups.entries()) {
    sourceGroups.push({
      sourceUrl: url,
      displayHost: getDisplayHost(url),
      results: items,
    })
  }
  sourceGroups.sort((a, b) => {
    const aDate = a.results[0]?.created_at ? new Date(a.results[0].created_at).getTime() : 0
    const bDate = b.results[0]?.created_at ? new Date(b.results[0].created_at).getTime() : 0
    return bDate - aDate // Descending (newest first)
  })

  for (const result of ungrouped) {
    sourceGroups.push({
      sourceUrl: '',
      displayHost: '',
      results: [result],
    })
  }

  return sourceGroups
}

// Color mapping for result types
const getResultTypeColor = (resultType: string | undefined) => {
  switch (resultType) {
    case 'document':
      return '#9b59b6'
    case 'batch':
      return '#3498db'
    default:
      return '#95a5a6'
  }
}

const getResultTypeLabel = (resultType: string | undefined) => {
  switch (resultType) {
    case 'document':
      return 'DOC'
    case 'batch':
      return 'BATCH'
    default:
      return null
  }
}

// How each verdict category reads in a multi-claim summary (app/database/schemas.py).
const VERDICT_SUMMARY: Record<
  VerdictCategory,
  { icon: string; label: string; bg: string; fg: string }
> = {
  supported: { icon: '\u2713', label: 'supported', bg: '#d5f4e6', fg: '#27ae60' },
  caveats: { icon: '\u26A0', label: 'missing caveats', bg: SCOPE_AMBER.bg, fg: SCOPE_AMBER.fg },
  narrower: { icon: '\u26A0', label: 'narrower scope', bg: SCOPE_AMBER.bg, fg: SCOPE_AMBER.fg },
  refuted: { icon: '\u2717', label: 'refuted', bg: '#fadbd8', fg: '#c0392b' },
  not_enough_info: { icon: '?', label: 'not enough info', bg: '#eef0f2', fg: '#6c7a89' },
  couldnt_check: { icon: '\u26A0', label: "couldn't check", bg: '#fdebd0', fg: '#ba4a00' },
}

const VERDICT_ORDER = Object.keys(VERDICT_SUMMARY) as VerdictCategory[]

// A single claim's pill takes the colours of its verdict category. Statuses
// with no category (a page that wasn't found, say) fall back to the symbol.
const pillColours = (
  status: string,
  counts?: Partial<Record<VerdictCategory, number>>
): { background: string; color: string } => {
  const category = VERDICT_ORDER.find((c) => counts?.[c])
  if (category) {
    return { background: VERDICT_SUMMARY[category].bg, color: VERDICT_SUMMARY[category].fg }
  }
  if (status.startsWith('\u2713')) return { background: '#d5f4e6', color: '#27ae60' }
  if (status.startsWith('\u26A0')) return { background: SCOPE_AMBER.bg, color: SCOPE_AMBER.fg }
  return { background: '#fadbd8', color: '#c0392b' }
}

const isMultiClaim = (counts?: Partial<Record<VerdictCategory, number>>): boolean =>
  Object.values(counts ?? {}).reduce((sum, n) => sum + (n ?? 0), 0) > 1

function VerdictSummary({
  counts,
  compact,
  reviewed,
}: {
  counts: Partial<Record<VerdictCategory, number>>
  compact?: boolean
  reviewed?: boolean
}) {
  const total = Object.values(counts).reduce((sum, n) => sum + (n ?? 0), 0)
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '0.4rem',
        marginBottom: '0.5rem',
        fontSize: compact ? '0.78rem' : '0.82rem',
      }}
    >
      <span style={{ color: '#57606a', fontWeight: 600 }}>
        {total} claims{reviewed ? ' (some human-reviewed)' : ''}:
      </span>
      {VERDICT_ORDER.filter((c) => counts[c]).map((c) => {
        const v = VERDICT_SUMMARY[c]
        return (
          <span
            key={c}
            style={{
              padding: '0.1rem 0.45rem',
              borderRadius: '4px',
              background: v.bg,
              color: v.fg,
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            {v.icon} {counts[c]} {v.label}
          </span>
        )
      })}
    </div>
  )
}

const ResultCard = ({
  result,
  compact = false,
  onToggleFavourite,
}: {
  result: VerificationListItem
  compact?: boolean
  onToggleFavourite?: (resultId: string) => void
}) => {
  const typeColor = getResultTypeColor(result.result_type)
  const typeLabel = getResultTypeLabel(result.result_type)

  return (
    <Link to={`/results/${result.display_id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
      <div
        style={{
          display: 'flex',
          border: '1px solid #ddd',
          borderRadius: compact ? '6px' : '8px',
          marginBottom: compact ? '0.5rem' : '0.75rem',
          background: 'white',
          cursor: 'pointer',
          transition: 'box-shadow 0.2s',
          overflow: 'hidden',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)'
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = 'none'
        }}
      >
        <div
          style={{
            width: typeLabel ? '4px' : '0',
            background: typeColor,
            flexShrink: 0,
          }}
        />

        <div
          style={{
            flex: 1,
            padding: compact ? '0.75rem 1rem' : '1rem 1.25rem',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '1rem',
          }}
        >
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: 'flex',
                gap: '0.75rem',
                marginBottom: '0.4rem',
                fontSize: '0.8rem',
                color: '#7f8c8d',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              {/* Star toggle */}
              {onToggleFavourite && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    onToggleFavourite(result.display_id)
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: '1rem',
                    lineHeight: 1,
                    color: result.is_favourite ? '#f39c12' : '#bdc3c7',
                    transition: 'color 0.15s',
                  }}
                  title={result.is_favourite ? 'Remove from favourites' : 'Add to favourites'}
                >
                  {result.is_favourite ? '\u2605' : '\u2606'}
                </button>
              )}
              {typeLabel && (
                <span
                  style={{
                    color: typeColor,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    letterSpacing: '0.5px',
                  }}
                >
                  {typeLabel}
                </span>
              )}
              <span>{new Date(result.created_at).toLocaleDateString()}</span>
              <span style={{ color: '#bdc3c7' }}>&bull;</span>
              <span>
                {result.fact_count} {result.fact_count === 1 ? 'fact' : 'facts'}
              </span>
              {result.has_human_validation && (
                <>
                  <span style={{ color: '#bdc3c7' }}>&bull;</span>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                      color:
                        result.human_validation_is_valid === true
                          ? '#27ae60'
                          : result.human_validation_is_valid === false
                            ? '#c0392b'
                            : '#6c757d',
                      fontWeight: 600,
                    }}
                    aria-label={`Human reviewed: ${
                      result.human_validation_is_valid === true
                        ? 'Valid'
                        : result.human_validation_is_valid === false
                          ? 'Invalid'
                          : 'No verdict'
                    }`}
                  >
                    {result.human_validation_is_valid === true
                      ? '\u2714'
                      : result.human_validation_is_valid === false
                        ? '\u2718'
                        : '\u25CB'}{' '}
                    Human Reviewed
                  </span>
                </>
              )}
              {result.is_public && (
                <>
                  <span style={{ color: '#bdc3c7' }}>&bull;</span>
                  <span style={{ color: '#27ae60', fontWeight: 600 }}>
                    {result.is_own === false ? 'Shared' : 'Public'}
                  </span>
                </>
              )}
              {/* Tag pills */}
              {result.tags && result.tags.length > 0 && (
                <>
                  <span style={{ color: '#bdc3c7' }}>&bull;</span>
                  {result.tags.map((tag) => (
                    <span
                      key={tag}
                      style={{
                        background: '#eef2f7',
                        color: '#4a6785',
                        padding: '0.1rem 0.4rem',
                        borderRadius: '8px',
                        fontSize: '0.7rem',
                        fontWeight: 500,
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </>
              )}
            </div>

            {/* Document title for document results */}
            {result.result_type === 'document' && result.document_title && (
              <p
                style={{
                  margin: '0 0 0.5rem 0',
                  color: '#2c3e50',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  lineHeight: '1.3',
                }}
              >
                {result.document_title}
              </p>
            )}

            {result.preview?.fact_text ? (
              <div>
                {/* Several claims: say how each came out, not just the first. */}
                {isMultiClaim(result.preview.counts) && (
                  <VerdictSummary
                    counts={result.preview.counts!}
                    compact={compact}
                    reviewed={result.has_human_validation}
                  />
                )}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  {!isMultiClaim(result.preview.counts) && (
                    <div
                      style={{
                        padding: '0.2rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: compact ? '0.8rem' : '0.85rem',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        ...pillColours(result.preview.status, result.preview.counts),
                        border: result.has_human_validation ? '2px solid #6c757d' : 'none',
                      }}
                    >
                      {result.preview.status}
                    </div>
                  )}
                  <p
                    style={{
                      margin: 0,
                      color: '#2c3e50',
                      fontSize: compact ? '0.85rem' : '0.9rem',
                      lineHeight: '1.4',
                      flex: 1,
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {result.preview.fact_text}
                  </p>
                </div>
                <ChipRow
                  chipKeys={toChipKeys(result.preview?.badges)}
                  style={{ marginTop: '0.5rem' }}
                />
              </div>
            ) : (
              <p style={{ margin: 0, color: '#95a5a6', fontStyle: 'italic' }}>
                Click to view details
              </p>
            )}
          </div>

          <div style={{ color: '#bdc3c7', fontSize: '1.2rem', alignSelf: 'center' }}>&rarr;</div>
        </div>
      </div>
    </Link>
  )
}

const SourceGroupCard = ({
  group,
  onToggleFavourite,
}: {
  group: SourceGroup
  onToggleFavourite?: (resultId: string) => void
}) => {
  const [expanded, setExpanded] = useState(false)
  const isSingle = group.results.length === 1

  if (isSingle && !group.sourceUrl) {
    return <ResultCard result={group.results[0]} onToggleFavourite={onToggleFavourite} />
  }

  if (isSingle) {
    return (
      <div style={{ marginBottom: '0.75rem' }}>
        <div
          style={{
            fontSize: '0.75rem',
            color: '#7f8c8d',
            marginBottom: '0.25rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          Source: {group.displayHost}
        </div>
        <ResultCard result={group.results[0]} onToggleFavourite={onToggleFavourite} />
      </div>
    )
  }

  const latestResult = group.results[0]
  const publicCount = group.results.filter((r) => r.is_public).length

  return (
    <div
      style={{
        marginBottom: '1rem',
        border: '1px solid #d5dbdb',
        borderRadius: '10px',
        overflow: 'hidden',
        background: '#fafbfc',
      }}
    >
      <div
        onClick={() => setExpanded(!expanded)}
        style={{
          padding: '0.75rem 1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          cursor: 'pointer',
          borderBottom: expanded ? '1px solid #eee' : 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1 }}>
          <span
            style={{
              background: '#e8ecef',
              color: '#555',
              padding: '0.2rem 0.5rem',
              borderRadius: '10px',
              fontSize: '0.75rem',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            {group.results.length} verifications
          </span>
          <span
            style={{
              fontSize: '0.8rem',
              color: '#7f8c8d',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {group.displayHost}
          </span>
          {publicCount > 0 && (
            <span
              style={{
                color: '#27ae60',
                fontSize: '0.7rem',
                fontWeight: 600,
              }}
            >
              {publicCount === group.results.length ? 'All Public' : `${publicCount} Public`}
            </span>
          )}
        </div>
        <span
          style={{
            color: '#95a5a6',
            fontSize: '0.9rem',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        >
          &#9660;
        </span>
      </div>

      {!expanded && (
        <div style={{ padding: '0.5rem 1rem 0.75rem' }}>
          <ResultCard result={latestResult} compact onToggleFavourite={onToggleFavourite} />
        </div>
      )}

      {expanded && (
        <div style={{ padding: '0.5rem 1rem 0.75rem' }}>
          {group.results.map((result) => (
            <ResultCard
              key={result.id}
              result={result}
              compact
              onToggleFavourite={onToggleFavourite}
            />
          ))}
        </div>
      )}
    </div>
  )
}

/** Filter chip button */
const FilterChip = ({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) => (
  <button
    onClick={onClick}
    style={{
      padding: '0.5rem 0.75rem',
      border: '1px solid #ddd',
      borderRadius: '20px',
      background: active ? '#2c3e50' : 'white',
      color: active ? 'white' : '#555',
      cursor: 'pointer',
      fontSize: '0.8rem',
      fontWeight: 500,
      transition: 'all 0.2s',
    }}
  >
    {label}
  </button>
)

export const PublicFacts = () => {
  const { isAuthenticated } = useAuth()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(0)
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [visibilityFilter, setVisibilityFilter] = useState<VisibilityFilter>('all')
  const [viewMode, setViewMode] = useState<ViewMode>('grouped')
  const [showHumanReviewedOnly, setShowHumanReviewedOnly] = useState(false)
  const [showFavouritesOnly, setShowFavouritesOnly] = useState(false)
  const [selectedTag, setSelectedTag] = useState<string>('')
  const [selectedProject, setSelectedProject] = useState<string>('')
  const limit = 20

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput)
      setPage(0)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchInput])

  // Fetch user's tags for the dropdown (only when authenticated)
  const { data: userTagsData } = useQuery({
    queryKey: ['userTags'],
    queryFn: () => tagsApi.listUserTags(),
    enabled: isAuthenticated,
  })
  const userTags = userTagsData?.tags ?? []

  // Fetch the org's projects for the dropdown (only when authenticated, #69)
  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.listProjects(),
    enabled: isAuthenticated,
  })
  const projects = projectsData?.projects ?? []

  // Favourite toggle mutation with optimistic update
  const favouriteMutation = useMutation({
    mutationFn: (resultId: string) => favouritesApi.toggleFavourite(resultId),
    onMutate: async (resultId: string) => {
      await queryClient.cancelQueries({ queryKey: ['results'] })
      const queries = queryClient.getQueriesData<{ results: VerificationListItem[] }>({
        queryKey: ['results'],
      })
      // Optimistically flip is_favourite in all cached result queries
      for (const [key, data] of queries) {
        if (!data?.results) continue
        queryClient.setQueryData(key, {
          ...data,
          results: data.results.map((r: VerificationListItem) =>
            r.display_id === resultId ? { ...r, is_favourite: !r.is_favourite } : r
          ),
        })
      }
      return { queries }
    },
    onError: (_error, _resultId, context) => {
      // Roll back on error
      if (context?.queries) {
        for (const [key, data] of context.queries) {
          queryClient.setQueryData(key, data)
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['results'] })
    },
  })

  const handleToggleFavourite = (resultId: string) => {
    favouriteMutation.mutate(resultId)
  }

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'results',
      page,
      searchQuery,
      statusFilter,
      visibilityFilter,
      showHumanReviewedOnly,
      showFavouritesOnly,
      selectedTag,
      selectedProject,
      isAuthenticated,
    ],
    queryFn: () => {
      if (isAuthenticated) {
        return resultsApi.listResults({
          limit,
          offset: page * limit,
          ...(searchQuery ? { q: searchQuery } : {}),
          status: statusFilter,
          visibility: visibilityFilter,
          ...(showHumanReviewedOnly ? { human_validated: true } : {}),
          ...(showFavouritesOnly ? { favourites_only: true } : {}),
          ...(selectedTag ? { tag: selectedTag } : {}),
          ...(selectedProject ? { project: selectedProject } : {}),
        })
      }
      // Not authenticated - only public endpoint available
      return resultsApi.searchPublicResults({
        limit,
        offset: page * limit,
        ...(searchQuery ? { q: searchQuery } : {}),
        status: statusFilter,
      })
    },
  })

  const results = useMemo(() => data?.results || [], [data?.results])
  const hasMore = results.length === limit

  const groups = useMemo(() => groupBySource(results), [results])
  const hasSourceUrls = results.some((r) => r.source_url)

  const visibilityOptions: { value: VisibilityFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'private', label: 'Private' },
    { value: 'my_public', label: 'My Public' },
    { value: 'all_public', label: 'All Public' },
  ]

  const statusOptions: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: 'All Statuses' },
    { value: 'verified', label: 'Verified' },
    { value: 'refuted', label: 'Refuted' },
  ]

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ margin: '0 0 0.5rem 0' }}>
          {isAuthenticated ? 'Fact Verification Results' : 'Explore Public Facts'}
        </h1>
        <p style={{ margin: 0, color: '#7f8c8d', fontSize: '0.9rem' }}>
          {isAuthenticated
            ? 'Your verification results and public facts from the community'
            : 'Browse fact verification results shared by the community'}
        </p>
      </div>

      {/* Search */}
      <div style={{ marginBottom: '0.75rem' }}>
        <input
          type="text"
          placeholder="Search facts..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          style={{
            width: '100%',
            padding: '0.6rem 1rem',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontSize: '0.9rem',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Filters row */}
      <div
        style={{
          display: 'flex',
          gap: '0.75rem',
          marginBottom: '1.5rem',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        {/* Visibility filters - only for authenticated users */}
        {isAuthenticated && (
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            {visibilityOptions.map((opt) => (
              <FilterChip
                key={opt.value}
                label={opt.label}
                active={visibilityFilter === opt.value}
                onClick={() => {
                  setVisibilityFilter(opt.value)
                  setPage(0)
                }}
              />
            ))}
          </div>
        )}

        {/* Separator */}
        {isAuthenticated && (
          <span style={{ color: '#ddd', fontSize: '1.2rem', lineHeight: 1 }}>|</span>
        )}

        {/* Status filters */}
        <div style={{ display: 'flex', gap: '0.25rem' }}>
          {statusOptions.map((opt) => (
            <FilterChip
              key={opt.value}
              label={opt.label}
              active={statusFilter === opt.value}
              onClick={() => {
                setStatusFilter(opt.value)
                setPage(0)
              }}
            />
          ))}
        </div>

        {/* Human reviewed + Favourites filters */}
        <span style={{ color: '#ddd', fontSize: '1.2rem', lineHeight: 1 }}>|</span>
        <FilterChip
          label="Human Reviewed"
          active={showHumanReviewedOnly}
          onClick={() => {
            setShowHumanReviewedOnly((prev) => !prev)
            setPage(0)
          }}
        />
        {isAuthenticated && (
          <FilterChip
            label="Favourites"
            active={showFavouritesOnly}
            onClick={() => {
              setShowFavouritesOnly((prev) => !prev)
              setPage(0)
            }}
          />
        )}

        {/* Tag filter dropdown */}
        {isAuthenticated && userTags.length > 0 && (
          <>
            <span style={{ color: '#ddd', fontSize: '1.2rem', lineHeight: 1 }}>|</span>
            <select
              value={selectedTag}
              onChange={(e) => {
                setSelectedTag(e.target.value)
                setPage(0)
              }}
              style={{
                padding: '0.5rem 0.75rem',
                border: `1px solid ${selectedTag ? '#2c3e50' : '#ddd'}`,
                borderRadius: '20px',
                background: selectedTag ? '#2c3e50' : 'white',
                color: selectedTag ? 'white' : '#555',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: 500,
                outline: 'none',
              }}
            >
              <option value="">All Tags</option>
              {userTags.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
          </>
        )}

        {/* Project filter dropdown (#69) */}
        {isAuthenticated && projects.length > 0 && (
          <>
            <span style={{ color: '#ddd', fontSize: '1.2rem', lineHeight: 1 }}>|</span>
            <select
              value={selectedProject}
              onChange={(e) => {
                setSelectedProject(e.target.value)
                setPage(0)
              }}
              style={{
                padding: '0.5rem 0.75rem',
                border: `1px solid ${selectedProject ? '#2c3e50' : '#ddd'}`,
                borderRadius: '20px',
                background: selectedProject ? '#2c3e50' : 'white',
                color: selectedProject ? 'white' : '#555',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: 500,
                outline: 'none',
              }}
            >
              <option value="">All Projects</option>
              {projects.map((p) => (
                <option key={p.id} value={p.name}>
                  {p.name} ({p.result_count})
                </option>
              ))}
            </select>
          </>
        )}

        {/* View mode toggle */}
        {hasSourceUrls && (
          <>
            <span style={{ color: '#ddd', fontSize: '1.2rem', lineHeight: 1 }}>|</span>
            <div style={{ display: 'flex', gap: '0.25rem' }}>
              <FilterChip
                label="Grouped"
                active={viewMode === 'grouped'}
                onClick={() => setViewMode('grouped')}
              />
              <FilterChip
                label="Flat"
                active={viewMode === 'flat'}
                onClick={() => setViewMode('flat')}
              />
            </div>
          </>
        )}
      </div>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <LoadingSpinner message="Loading results..." />
        </div>
      ) : error ? (
        <div style={{ color: '#e74c3c' }}>Error loading results: {error.message}</div>
      ) : results.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '3rem',
            background: '#ecf0f1',
            borderRadius: '8px',
          }}
        >
          <p style={{ fontSize: '1.2rem', color: '#555' }}>
            {searchQuery ? 'No matching facts found' : 'No verification results yet'}
          </p>
          <p style={{ color: '#7f8c8d' }}>
            {searchQuery
              ? 'Try a different search term or broaden your filters.'
              : isAuthenticated
                ? 'Results from fact verification will appear here. Use the Verify Claims page or MCP tools to get started.'
                : 'When users mark their fact verifications as public, they will appear here for everyone to browse.'}
          </p>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: '2rem' }}>
            {viewMode === 'grouped'
              ? groups.map((group, idx) => (
                  <SourceGroupCard
                    key={group.sourceUrl || `ungrouped-${idx}`}
                    group={group}
                    onToggleFavourite={isAuthenticated ? handleToggleFavourite : undefined}
                  />
                ))
              : results.map((result: VerificationListItem) => (
                  <ResultCard
                    key={result.id}
                    result={result}
                    onToggleFavourite={isAuthenticated ? handleToggleFavourite : undefined}
                  />
                ))}
          </div>

          {/* Pagination */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              style={{
                background: page === 0 ? '#95a5a6' : '#3498db',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: page === 0 ? 'not-allowed' : 'pointer',
              }}
            >
              Previous
            </button>
            <span style={{ display: 'flex', alignItems: 'center' }}>Page {page + 1}</span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasMore}
              style={{
                background: !hasMore ? '#95a5a6' : '#3498db',
                color: 'white',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '4px',
                cursor: !hasMore ? 'not-allowed' : 'pointer',
              }}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  )
}
